/**
 * Fund Detail Header Component
 *
 * Displays fund name, AMC, rating badge, and action buttons (Copy ISIN, Add to Watchlist).
 * Used at the top of the fund detail screen.
 */

import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatINRCompact, formatPercent } from "@/lib/utils";
import { Copy, Check, Star, TrendingUp, TrendingDown } from "lucide-react";
import type { FundRating, FundCategory, FundPlanType } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Fund header data for display
 */
export interface FundDetailHeaderData {
  /** Fund ISIN */
  isin: string;
  /** Fund name */
  name: string;
  /** Asset Management Company name */
  amcName: string;
  /** Current rating */
  rating: FundRating;
  /** Fund category */
  category: FundCategory;
  /** Plan type (direct/regular) */
  planType: FundPlanType;
  /** Current NAV */
  nav: number;
  /** NAV date (ISO string) */
  navDate: string;
  /** 1-day return percentage */
  returns1D: number;
  /** Rank in category */
  rank: number;
  /** Total funds in category */
  totalFundsInCategory: number;
}

/**
 * Props for the FundDetailHeader component
 */
export interface FundDetailHeaderProps {
  /** Fund header data */
  data: FundDetailHeaderData;
  /** Whether fund is in watchlist */
  isWatchlisted?: boolean;
  /** Callback when watchlist button is clicked */
  onWatchlistToggle?: () => void;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Rating configuration with colors and labels
 */
const RATING_CONFIG: Record<
  FundRating,
  {
    label: string;
    badgeBg: string;
    badgeText: string;
    badgeBorder: string;
  }
> = {
  "in-form": {
    label: "In-Form",
    badgeBg: "bg-emerald-100 dark:bg-emerald-900/30",
    badgeText: "text-emerald-700 dark:text-emerald-400",
    badgeBorder: "border-emerald-200 dark:border-emerald-800",
  },
  "on-track": {
    label: "On-Track",
    badgeBg: "bg-sky-100 dark:bg-sky-900/30",
    badgeText: "text-sky-700 dark:text-sky-400",
    badgeBorder: "border-sky-200 dark:border-sky-800",
  },
  "off-track": {
    label: "Off-Track",
    badgeBg: "bg-amber-100 dark:bg-amber-900/30",
    badgeText: "text-amber-700 dark:text-amber-400",
    badgeBorder: "border-amber-200 dark:border-amber-800",
  },
  "out-of-form": {
    label: "Out-of-Form",
    badgeBg: "bg-rose-100 dark:bg-rose-900/30",
    badgeText: "text-rose-700 dark:text-rose-400",
    badgeBorder: "border-rose-200 dark:border-rose-800",
  },
};

/**
 * Category labels for display
 */
const CATEGORY_LABELS: Record<FundCategory, string> = {
  "equity-large-cap": "Large Cap",
  "equity-mid-cap": "Mid Cap",
  "equity-small-cap": "Small Cap",
  "equity-flexi-cap": "Flexi Cap",
  "equity-multi-cap": "Multi Cap",
  "equity-elss": "ELSS",
  "equity-sectoral": "Sectoral",
  "equity-global": "Global",
  "debt-liquid": "Liquid",
  "debt-short-term": "Short Duration",
  "debt-medium-term": "Medium Duration",
  "debt-long-term": "Long Duration",
  "debt-gilt": "Gilt",
  "debt-corporate": "Corporate Bond",
  "hybrid-aggressive": "Aggressive Hybrid",
  "hybrid-balanced": "Balanced Hybrid",
  "hybrid-conservative": "Conservative Hybrid",
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format NAV date for display
 */
function formatNavDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Fund Detail Header
 *
 * Shows fund name, AMC, rating badge, NAV info, and action buttons.
 *
 * @example
 * ```tsx
 * <FundDetailHeader
 *   data={{
 *     isin: "INF123K01234",
 *     name: "PPFAS Flexi Cap Fund - Direct Plan",
 *     amcName: "PPFAS Mutual Fund",
 *     rating: "in-form",
 *     category: "equity-flexi-cap",
 *     planType: "direct",
 *     nav: 85.42,
 *     navDate: "2024-12-20",
 *     returns1D: 0.45,
 *     rank: 3,
 *     totalFundsInCategory: 45,
 *   }}
 *   onWatchlistToggle={() => console.log("Toggle watchlist")}
 * />
 * ```
 */
export function FundDetailHeader({
  data,
  isWatchlisted = false,
  onWatchlistToggle,
  className,
}: FundDetailHeaderProps) {
  const [isCopied, setIsCopied] = useState(false);
  const ratingConfig = RATING_CONFIG[data.rating];
  const categoryLabel = CATEGORY_LABELS[data.category];
  const isPositiveReturn = data.returns1D >= 0;

  const handleCopyIsin = async () => {
    try {
      await navigator.clipboard.writeText(data.isin);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy ISIN:", err);
    }
  };

  return (
    <Card className={cn("w-full", className)}>
      <CardContent className="p-4 sm:p-6">
        {/* Top Section: Name, Rating, Actions */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          {/* Fund Info */}
          <div className="flex-1 min-w-0">
            {/* Fund Name */}
            <h1 className="text-lg sm:text-xl font-semibold text-foreground truncate">
              {data.name}
            </h1>

            {/* AMC Name */}
            <p className="text-sm text-muted-foreground mt-1">{data.amcName}</p>

            {/* Category and Plan Type Tags */}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Badge variant="secondary" className="text-xs">
                {categoryLabel}
              </Badge>
              <Badge variant="outline" className="text-xs capitalize">
                {data.planType}
              </Badge>
              {/* Rating Badge */}
              <Badge
                className={cn(
                  "font-medium border text-xs",
                  ratingConfig.badgeBg,
                  ratingConfig.badgeText,
                  ratingConfig.badgeBorder
                )}
              >
                {ratingConfig.label}
              </Badge>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Copy ISIN Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyIsin}
              className="text-xs gap-1.5"
            >
              {isCopied ? (
                <>
                  <Check className="h-3.5 w-3.5 text-green-600" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  ISIN
                </>
              )}
            </Button>

            {/* Watchlist Button */}
            {onWatchlistToggle && (
              <Button
                variant={isWatchlisted ? "default" : "outline"}
                size="sm"
                onClick={onWatchlistToggle}
                className="text-xs gap-1.5"
              >
                <Star
                  className={cn(
                    "h-3.5 w-3.5",
                    isWatchlisted && "fill-current"
                  )}
                />
                {isWatchlisted ? "Watchlisted" : "Watchlist"}
              </Button>
            )}
          </div>
        </div>

        {/* Bottom Section: NAV and Rank Info */}
        <div className="mt-4 pt-4 border-t border-border/50">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* NAV Info */}
            <div>
              <p className="text-xs text-muted-foreground">NAV</p>
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-bold">
                  {formatINRCompact(data.nav)}
                </span>
                <span
                  className={cn(
                    "flex items-center text-sm font-medium",
                    isPositiveReturn ? "text-green-600" : "text-red-600"
                  )}
                >
                  {isPositiveReturn ? (
                    <TrendingUp className="h-3.5 w-3.5 mr-0.5" />
                  ) : (
                    <TrendingDown className="h-3.5 w-3.5 mr-0.5" />
                  )}
                  {formatPercent(data.returns1D)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                As of {formatNavDate(data.navDate)}
              </p>
            </div>

            {/* Rank Info */}
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Category Rank</p>
              <div className="flex items-baseline gap-1">
                <span className="text-xl font-bold">#{data.rank}</span>
                <span className="text-sm text-muted-foreground">
                  / {data.totalFundsInCategory}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {categoryLabel}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for FundDetailHeader
 */
export function FundDetailHeaderSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <Card className={cn("w-full", className)}>
      <CardContent className="p-4 sm:p-6">
        {/* Top Section */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1 min-w-0 space-y-2">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-5 w-14 rounded-full" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-8 w-24" />
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-4 pt-4 border-t border-border/50">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Skeleton className="h-3 w-8" />
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-3 w-24" />
            </div>
            <div className="text-right space-y-1">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-6 w-16" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default FundDetailHeader;
